import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, Star, ArrowRight } from 'lucide-react';
import MainHeader from '../../Components/Header/MainHeader';
import MainFooter from '../../Components/Footer/MainFooter';

export default function ContactUs() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    interest: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        interest: '',
        message: ''
      });
    }, 3000);
  };

  const interests = [
    'Buying A Property',
    'Renting A Property',
    'Listing A Property As An Agent',
    'Listing A Property As A Homeowner',
    'Hiring A Fundi',
    'Joining As A Fundi',
    'General Enquiry',
    'Technical Support'
  ];

  const contactInfo = [
    {
      icon: Phone,
      title: 'Call Us',
      details: '0733 212 124',
      subtitle: 'Monday to Saturday',
      action: 'tel:0733212124'
    },
    {
      icon: Mail,
      title: 'Email Us',
      details: 'info@rekebisha.com',
      subtitle: 'We reply within 2 hours',
      action: 'mailto:info@rekebisha.com'
    },
    {
      icon: MapPin,
      title: 'Visit Us',
      details: 'Nairobi, Kenya',
      subtitle: 'Serving all major cities',
      action: '#'
    },
    {
      icon: Clock,
      title: 'Working Hours',
      details: '8am to 6pm',
      subtitle: 'Monday through Saturday',
      action: '#'
    }
  ];

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <MainHeader />

      {/* Hero Section */}
      <section
        className="relative bg-cover bg-center"
        style={{ backgroundImage: `url(https://images.unsplash.com/photo-1554995207-c18c203602cb?q=80&w=1600&auto=format&fit=crop)` }}
      >
        <div className="absolute inset-0 bg-navy/75"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Get In Touch With Us
          </h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto">
            Have a question about buying, renting, listing a property or hiring a fundi? Our team is ready to help.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="w-14 h-14 bg-brandgreen/10 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <info.icon className="w-7 h-7 text-brandgreen" />
                </div>
                <h3 className="text-xl font-bold text-navy mb-2 text-center">{info.title}</h3>
                <div className="text-center">
                  {info.action.startsWith('#') ? (
                    <div className="text-lg font-semibold text-navy mb-1">{info.details}</div>
                  ) : (
                    <a
                      href={info.action}
                      className="text-lg font-semibold text-navy hover:text-brandgreen block mb-1 transition-colors duration-300"
                    >
                      {info.details}
                    </a>
                  )}
                  <div className="text-sm text-gray-600">{info.subtitle}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Form */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border-t-4 border-brandgreen">
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-navy mb-4">Send Us A Message</h2>
                <p className="text-gray-600">Fill out the form below and we will get back to you within 2 hours.</p>
              </div>

              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-brandgreen/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-12 h-12 text-brandgreen" />
                  </div>
                  <h3 className="text-2xl font-bold text-brandgreen mb-4">Message Sent Successfully</h3>
                  <p className="text-gray-600">Thank you for reaching out. We will contact you soon.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-bold text-navy mb-2">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-brandgreen focus:outline-none transition-colors duration-300"
                        placeholder="Enter your full name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-navy mb-2">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-brandgreen focus:outline-none transition-colors duration-300"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-bold text-navy mb-2">Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-brandgreen focus:outline-none transition-colors duration-300"
                        placeholder="0700 000 000"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-navy mb-2">I Am Interested In</label>
                      <select
                        name="interest"
                        value={formData.interest}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-brandgreen focus:outline-none transition-colors duration-300"
                      >
                        <option value="">Select an option</option>
                        {interests.map((interest, index) => (
                          <option key={index} value={interest}>{interest}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-navy mb-2">Message *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-brandgreen focus:outline-none transition-colors duration-300 resize-none"
                      placeholder="Tell us how we can help you"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-brandgreen text-white font-bold py-4 px-8 rounded-xl hover:bg-brandgreen-dark transition-colors duration-300 flex items-center justify-center"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    Send Message
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </button>
                </form>
              )}
            </div>

            {/* Additional Info */}
            <div className="space-y-8">
              <div className="bg-navy rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-6">Why Choose Rekebisha</h3>
                <div className="space-y-4">
                  {[
                    'Direct Contact With Homeowners, Agents And Fundis',
                    '1,000+ Verified Property Listings',
                    '2,000+ Verified Fundis',
                    'Coverage Across 47 Counties',
                  ].map((item, index) => (
                    <div key={index} className="flex items-center">
                      <div className="w-8 h-8 bg-brandgreen rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                        <CheckCircle className="w-5 h-5 text-white" />
                      </div>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-lg p-8 border-l-4 border-brandgreen">
                <h3 className="text-2xl font-bold text-navy mb-4">Quick Contact</h3>
                <p className="text-gray-600 mb-6">Need to reach us right away? Use the options below.</p>
                <div className="space-y-4">
                  <a
                    href="tel:0733212124"
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors duration-300 group"
                  >
                    <div className="flex items-center">
                      <Phone className="w-6 h-6 text-brandgreen mr-3" />
                      <div>
                        <div className="font-bold text-navy">Call Now</div>
                        <div className="text-sm text-gray-600">0733 212 124</div>
                      </div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-brandgreen group-hover:translate-x-1 transition-transform duration-300" />
                  </a>

                  <a
                    href="mailto:info@rekebisha.com"
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors duration-300 group"
                  >
                    <div className="flex items-center">
                      <Mail className="w-6 h-6 text-brandgreen mr-3" />
                      <div>
                        <div className="font-bold text-navy">Email Us</div>
                        <div className="text-sm text-gray-600">info@rekebisha.com</div>
                      </div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-brandgreen group-hover:translate-x-1 transition-transform duration-300" />
                  </a>
                </div>
              </div>

              <div className="bg-gray-50 rounded-2xl p-6 border-2 border-gray-100">
                <div className="flex items-center mb-4">
                  <Star className="w-6 h-6 text-brandgreen mr-2" />
                  <h4 className="font-bold text-navy">Response Time</h4>
                </div>
                <p className="text-gray-600 text-sm">
                  We aim to respond to every enquiry within 2 hours during business hours. Your search for a home matters to us.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <MainFooter />
    </div>
  );
}
